import { useCallback } from 'react';
import { TimeSignal, TimeSignalClassification } from 'graphql/types';
import { DateRange } from 'types/date-range';
import { useNotesAnimationContext } from 'modules/notes/providers';
import { useBulkUpdateTimeSignals } from '../../hooks/useBulkUpdateTimeSignals';
import { useBulkDeleteTimeSignals } from '../../hooks/useBulkDeleteTimeSignals';

export type NotesActions =
  | keyof typeof TimeSignalClassification
  | 'DeleteForever';

export type TimeSignalActionArgs = {
  classificationTarget: NotesActions;
  isAllSelected: boolean;
  selectedNotes: string[];
  unSelectedNotes?: string[];
  dateRange?: DateRange;
  note?: TimeSignal;
};
export type HandleTimeSingalActionCallback = (
  args: TimeSignalActionArgs
) => Promise<void>;

type ReturnType = {
  handleTimeSignalAction: HandleTimeSingalActionCallback;
  isActionInProgress: boolean;
};

export const useTimeSignalActions = (
  clearSelectedNotes?: () => void
): ReturnType => {
  const {
    onTimeSignalClassificationUpdate,
    updateInProgress
  } = useBulkUpdateTimeSignals();

  const {
    onTimeSignalsBulkDelete,
    deleteInProgress
  } = useBulkDeleteTimeSignals();

  const { setNotesToAnimate } = useNotesAnimationContext();

  const handleTimeSignalAction = useCallback(
    async ({
      selectedNotes,
      unSelectedNotes,
      classificationTarget,
      note
    }: TimeSignalActionArgs) => {
      setNotesToAnimate(
        selectedNotes.map((id) => ({
          id: id,
          action: 'Move',
          folder: classificationTarget
        }))
      );
      if (classificationTarget === 'DeleteForever') {
        await onTimeSignalsBulkDelete({
          selectedNoteIds: selectedNotes,
          unSelectedNoteIds: unSelectedNotes ?? [],
          note
        });
      } else {
        await onTimeSignalClassificationUpdate({
          unSelectedNoteIds: unSelectedNotes ?? [],
          selectedNoteIds: selectedNotes,
          classificationTarget: TimeSignalClassification[classificationTarget],
          note
        });
        if (clearSelectedNotes) clearSelectedNotes();
      }
      setNotesToAnimate([]);
    },
    [
      clearSelectedNotes,
      onTimeSignalClassificationUpdate,
      onTimeSignalsBulkDelete,
      setNotesToAnimate
    ]
  );
  return {
    handleTimeSignalAction: handleTimeSignalAction,
    isActionInProgress: updateInProgress || deleteInProgress
  };
};
